import jsPDF from 'jspdf';
import moment from 'moment';
import {TFunction} from 'i18next';
import sioslifeLogo from '../../../../logos/sioslife.png';
import {User} from '../../../../backend/interfaces/user';
import {
  pdfAddCircularInitialsAvatar,
  pdfAddCircularImage,
  pdfAddField,
  pdfAddHorizontalLine,
  pdfAddText,
} from './export-pdf-footer-header-utils';
import {translations} from '../../../../i18n/translations';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {PDF_FONT_MAIN_COLOR} from './export-pdf-font';

export type PDFHeaderInfoOpts = {
  startintPos?: {
    x: number;
    y: number;
  };
  pictureSize?: number;
};

export function pdfHeaderInfo(
  pdf: jsPDF,
  user: User | undefined,
  entity: EntityInfo | undefined,
  t: TFunction
) {
  // Left: User Info or Brand
  if (user) {
    pdfHeaderUserInfo(pdf, user, t);
  } else {
    pdf.addImage(sioslifeLogo, 'PNG', 14, 3.4, 31.5, 14);
  }

  // Right: Entity Info
  pdfHeaderEntityInfo(pdf, entity, t);

  // Horizontal line
  pdfAddHorizontalLine(pdf, 14, 21.2);
}

function pdfHeaderUserInfo(
  pdf: jsPDF,
  user: User | undefined,
  t: TFunction,
  opts?: PDFHeaderInfoOpts
): number {
  if (!user) {
    // No user info
    return 0;
  }

  pdf.setFontSize(8);
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);
  let userPosX = opts?.startintPos?.x ?? 14;
  let userPosY = opts?.startintPos?.y ?? 2.5;
  const userPictureSize = opts?.pictureSize ?? 14;
  const userInitials = getUserInitials(user.name);

  const addInlineField = (label: string, value: string, x: number, y: number) => {
    if (!value) {
      return x;
    }

    const fieldLabel = `${label}: `;
    pdfAddText(pdf, fieldLabel, x, y, {font: {style: 'bold'}});
    const nextX = x + pdf.getTextWidth(fieldLabel);
    pdfAddText(pdf, value, nextX, y);

    return nextX + pdf.getTextWidth(value);
  };

  // ---- User Picture ----
  if (user.picture) {
    pdfAddCircularImage(pdf, user.picture, userPosX, userPosY, userPictureSize);
    // positions for texts
    userPosX += userPictureSize + 3; // margin between picture and text
  } else if (userInitials) {
    pdfAddCircularInitialsAvatar(pdf, userInitials, userPosX, userPosY, userPictureSize);
    userPosX += userPictureSize + 3; // margin between picture and text
  }

  // ---- User Name ----
  userPosY += 3; // position to align with picture(top align).
  pdfAddField(pdf, user.name, userPosX, userPosY);
  userPosY += 4.3; // blank space after name

  // ---- User birthday and age ----
  if (user.birthday) {
    const age = moment().diff(user.birthday, 'years');
    const birthday = moment(user.birthday).format('DD/MM/YYYY');
    const birthdayText =
      age >= 0 ? `${birthday} (${age} ${t(translations.cards.years)})` : birthday;

    pdfAddField(pdf, t(translations.user_form.birthday_input), birthdayText, userPosX, userPosY);
    userPosY += 3.2;
  }

  // ---- User address ----
  if (user.address) {
    pdfAddField(
      pdf,
      t(translations.export.address),
      user.address.split('\n').join(' '),
      userPosX,
      userPosY
    );
    userPosY += 3.2;
  }

  // ---- Tax payer number and SNS number ----
  if (user.taxPayerNumber || user.nationalHealthServiceNumber) {
    let currentPosX = addInlineField(
      t(translations.export.contributor_number),
      user.taxPayerNumber ?? '',
      userPosX,
      userPosY
    );

    if (user.taxPayerNumber && user.nationalHealthServiceNumber) {
      pdfAddText(pdf, ' | ', currentPosX, userPosY);
      currentPosX += pdf.getTextWidth(' | ');
    }

    addInlineField(
      t(translations.export.sns_number),
      user.nationalHealthServiceNumber ?? '',
      currentPosX,
      userPosY
    );

    userPosY += 3.2;
  }

  // Phone number
  if (user.phoneNumber) {
    pdfAddField(pdf, t(translations.export.contact), user.phoneNumber, userPosX, userPosY);
    userPosY += 3.2;
  }

  return userPosY;
}

export function pdfHeaderEntityInfo(
  pdf: jsPDF,
  entity: EntityInfo | undefined,
  t: TFunction,
  opts?: PDFHeaderInfoOpts
): number {
  if (!entity) {
    // No entity info
    return 0;
  }

  pdf.setFontSize(8);
  pdf.setTextColor(PDF_FONT_MAIN_COLOR);
  let posX = opts?.startintPos?.x ?? 14;
  let posY = opts?.startintPos?.y ?? 2.5;
  const PicSize = opts?.pictureSize ?? 14;

  // Adjust X to start from the right side
  const pageWidth = pdf.internal.pageSize.width;
  posX = pageWidth - posX;

  // Entity Picture
  if (entity?.picture) {
    posX -= PicSize;
    pdfAddCircularImage(pdf, entity.picture, posX, posY, PicSize);
    posX -= 3; // margin between picture and text
  }

  // Entity Name
  posY += 3; // position to align with picture top.
  pdfAddText(pdf, entity.shortName ?? entity.name, posX, posY, {
    font: {style: 'bold'},
    textOptions: {align: 'right'},
  });
  posY += 4.3; // blank space after name

  // Entity address/name
  pdfAddText(pdf, entity.address?.split('\n').join(' ') ?? entity.name, posX, posY, {
    textOptions: {align: 'right'},
  });
  posY += 3.2;

  // Entity email
  if (entity.email) {
    pdfAddText(pdf, entity.email, posX, posY, {
      textOptions: {align: 'right'},
    });
    posY += 3.5;
  }

  if (entity.mobilePhoneNumber) {
    pdfAddText(pdf, entity.mobilePhoneNumber, posX, posY, {
      textOptions: {align: 'right'},
    });
    posY += 3.2;
  }

  return posY;
}

function getUserInitials(name: string | undefined): string {
  if (!name) {
    return '';
  }

  const nameParts = name.trim().split(/\s+/).filter(Boolean);

  if (!nameParts.length) {
    return '';
  }

  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  }

  return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
}
